// store/Plans/PlansReducer.js
import * as actionTypes from "../ActionTypes";

const initialState = {
  plans: [],
  userPlans: [],
  twgPlans: [],
  followedEntityPlans: [],
  sermonStudyJobs: [],
  sermonStudyJobsMeta: {
    totalRecords: 0,
    page: 1,
    limit: 10,
  },
  planDetails: null,
  loading: false,
  success: false,
  error: null,
};

const normalizePlansPayload = (payloadData) => {
  if (Array.isArray(payloadData)) return payloadData;
  if (payloadData && typeof payloadData === "object") return [payloadData];
  return [];
};

const getPrimaryPlan = (payloadData) => {
  if (Array.isArray(payloadData)) return payloadData[0] || null;
  if (Array.isArray(payloadData?.data)) return payloadData.data[0] || null;
  if (payloadData && typeof payloadData === "object") return payloadData;
  return null;
};

const plansReducer = (state = initialState, action) => {
  switch (action.type) {
    /* ================= SAVE PLAN ================= */
    case actionTypes.SAVE_PLAN_REQUEST:
    case actionTypes.UPDATE_PLAN_REQUEST:
      return {
        ...state,
        loading: true,
        success: false,
        error: null,
      };

    case actionTypes.SAVE_PLAN_SUCCESS:
    case actionTypes.UPDATE_PLAN_SUCCESS:
      return {
        ...state,
        loading: false,
        success: true,
      };

    case actionTypes.SAVE_PLAN_FAIL:
    case actionTypes.UPDATE_PLAN_FAIL:
      return {
        ...state,
        loading: false,
        success: false,
        error: action.payload,
      };

    /* ================= READ PLAN ================= */
    case actionTypes.READ_PLAN_REQUEST:
      return {
        ...state,
        loading: true,
        planDetails: null,
        error: null,
      };

    case actionTypes.READ_PLAN_SUCCESS:
      return {
        ...state,
        loading: false,
        plans: normalizePlansPayload(action.payload?.data),
        planDetails: getPrimaryPlan(action.payload?.data),
      };

    case actionTypes.READ_PLAN_FAIL:
      return {
        ...state,
        loading: false,
        planDetails: null,
        error: action.payload,
      };

    /* ================= READ USER PLANS ================= */
    case actionTypes.READ_USER_PLANS_REQUEST:
      return {
        ...state,
        loading: true,
        error: null,
      };

    case actionTypes.READ_USER_PLANS_SUCCESS:
      return {
        ...state,
        loading: false,
        userPlans: action.payload?.data || [],
      };

    case actionTypes.READ_USER_PLANS_FAIL:
      return {
        ...state,
        loading: false,
        error: action.payload,
      };

    /* ================= READ TWG PLANS ================= */
    case actionTypes.READ_TWG_PLANS_REQUEST:
      return {
        ...state,
        loading: true,
        error: null,
      };

    case actionTypes.READ_TWG_PLANS_SUCCESS:
      return {
        ...state,
        loading: false,
        twgPlans: action.payload?.data || [],
      };

    case actionTypes.READ_TWG_PLANS_FAIL:
      return {
        ...state,
        loading: false,
        error: action.payload,
      };

    /* ================= READ FOLLOWED ENTITY PLANS ================= */
    case actionTypes.READ_FOLLOWED_ENTITY_PLANS_REQUEST:
      return {
        ...state,
        loading: true,
        error: null,
      };

    case actionTypes.READ_FOLLOWED_ENTITY_PLANS_SUCCESS:
      return {
        ...state,
        loading: false,
        followedEntityPlans: action.payload?.data || [],
      };

    case actionTypes.READ_FOLLOWED_ENTITY_PLANS_FAIL:
      return {
        ...state,
        loading: false,
        error: action.payload,
      };

    /* ================= READ USER SERMON STUDY JOBS ================= */
    case actionTypes.READ_USER_SERMON_STUDY_JOBS_REQUEST:
      return {
        ...state,
        loading: true,
        error: null,
      };

    case actionTypes.READ_USER_SERMON_STUDY_JOBS_SUCCESS: {
      const jobsData = Array.isArray(action.payload?.data)
        ? action.payload.data
        : action.payload?.data
        ? [action.payload.data]
        : [];

      return {
        ...state,
        loading: false,
        sermonStudyJobs: jobsData,
        sermonStudyJobsMeta: {
          totalRecords: Number(action.payload?.totalRecords) || jobsData.length,
          page: Number(action.payload?.page) || 1,
          limit: Number(action.payload?.limit) || 10,
        },
      };
    }

    case actionTypes.READ_USER_SERMON_STUDY_JOBS_FAIL:
      return {
        ...state,
        loading: false,
        error: action.payload,
      };

    /* ================= SAVE DAY PLAN ================= */
    case actionTypes.SAVE_DAY_PLAN_REQUEST:
      return {
        ...state,
        loading: true,
        success: false,
        error: null,
      };

    case actionTypes.SAVE_DAY_PLAN_SUCCESS:
      return {
        ...state,
        loading: false,
        success: true,
      };

    case actionTypes.SAVE_DAY_PLAN_FAIL:
      return {
        ...state,
        loading: false,
        success: false,
        error: action.payload,
      };

    /* ================= UPDATE/DELETE DAY PLAN ================= */
    case actionTypes.UPDATE_DAY_PLAN_REQUEST:
    case actionTypes.UPDATE_DAY_PLAN_PROGRESS_REQUEST:
    case actionTypes.DELETE_DAY_PLAN_REQUEST:
    case actionTypes.SAVE_DAY_PLAN_DETAILS_REQUEST:
    case actionTypes.UPDATE_DAY_PLAN_DETAILS_REQUEST:
    case actionTypes.DELETE_DAY_PLAN_DETAILS_REQUEST:
      return {
        ...state,
        loading: true,
        success: false,
        error: null,
      };

    case actionTypes.UPDATE_DAY_PLAN_SUCCESS:
    case actionTypes.UPDATE_DAY_PLAN_PROGRESS_SUCCESS:
    case actionTypes.DELETE_DAY_PLAN_SUCCESS:
    case actionTypes.SAVE_DAY_PLAN_DETAILS_SUCCESS:
    case actionTypes.UPDATE_DAY_PLAN_DETAILS_SUCCESS:
    case actionTypes.DELETE_DAY_PLAN_DETAILS_SUCCESS:
      {
        const progressUpdate = action.payload?.data;
        const updatedDay = progressUpdate?.updatedDay;
        const completion = progressUpdate?.completion;
        const normalizedItemProgressMap = Object.fromEntries(
          Object.entries(updatedDay?.itemProgressMap || {}).map(([key, value]) => [
            key,
            Number(value) || 0,
          ])
        );

        const nextPlans = updatedDay
          ? state.plans.map((plan) => {
              if (plan?._id !== progressUpdate?.planObjId) return plan;

              const nextDayplans = Array.isArray(plan?.dayplans)
                ? plan.dayplans.map((day) =>
                    day?.dayplanObjId === updatedDay?.dayplanObjId
                      ? {
                          ...day,
                          userProgress: {
                            ...day?.userProgress,
                            ...updatedDay,
                            progress: Number(updatedDay?.progress) || 0,
                            itemProgressMap: normalizedItemProgressMap,
                          },
                        }
                      : day
                  )
                : plan?.dayplans;

              return {
                ...plan,
                dayplans: nextDayplans,
                userPlanProgress: completion
                  ? {
                      ...plan?.userPlanProgress,
                      completedDayplans:
                        Number(completion?.completedDayplans) || 0,
                      totalDayplans:
                        Number(completion?.totalDayplans) ||
                        plan?.userPlanProgress?.totalDayplans ||
                        0,
                      completionPercentage:
                        Number(completion?.percentage) || 0,
                      isPlanCompleted: Boolean(completion?.isPlanCompleted),
                    }
                  : plan?.userPlanProgress,
              };
            })
          : state.plans;

        return {
        ...state,
        loading: false,
        success: true,
        plans: nextPlans,
      };
      }

    case actionTypes.UPDATE_DAY_PLAN_FAIL:
    case actionTypes.UPDATE_DAY_PLAN_PROGRESS_FAIL:
    case actionTypes.DELETE_DAY_PLAN_FAIL:
    case actionTypes.SAVE_DAY_PLAN_DETAILS_FAIL:
    case actionTypes.UPDATE_DAY_PLAN_DETAILS_FAIL:
    case actionTypes.DELETE_DAY_PLAN_DETAILS_FAIL:
      return {
        ...state,
        loading: false,
        success: false,
        error: action.payload,
      };

    /* ================= CREATE YOUTUBE PLAN ================= */
    case actionTypes.CREATE_YOUTUBE_PLAN_REQUEST:
      return {
        ...state,
        loading: true,
        success: false,
        error: null,
      };

    case actionTypes.CREATE_YOUTUBE_PLAN_SUCCESS:
      return {
        ...state,
        loading: false,
        success: true,
      };

    case actionTypes.CREATE_YOUTUBE_PLAN_FAIL:
    case actionTypes.RESET_SERMON_STUDY_RETRY_FAIL:
      return {
        ...state,
        loading: false,
        success: false,
        error: action.payload,
      };

    case actionTypes.RESET_SERMON_STUDY_RETRY_REQUEST:
      return {
        ...state,
        loading: true,
        success: false,
        error: null,
      };

    case actionTypes.RESET_SERMON_STUDY_RETRY_SUCCESS:
      return {
        ...state,
        loading: false,
        success: true,
      };

    default:
      return state;
  }
};

export default plansReducer;
